<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import * as Dialog from '$lib/components/ui/dialog';
  import * as Popover from '$lib/components/ui/popover';
  import { Button } from '$lib/components/ui/button/index.js';
  import { focusMainButton } from '@/Util/utils';
  import { getPredictiveState, setPredictiveChoice, type PredictiveState } from './predictive';
  import PredictiveModeSummary from './PredictiveModeSummary.svelte';

  // The athlete's own username on this page: the prompt is about their plan, not the one a coach is looking at.
  export let username: string;

  let open = false;
  let busy = false;
  let failed = false;
  let predictive: PredictiveState | null = null;

  onMount(async () => {
    const state = await getPredictiveState();
    predictive = state;
    open = !!state && state.ask && state.username === username;
    // Continue, not the What is Predictive Mode? link before it
    if (open) focusMainButton();
  });

  async function choose(choice: 'on' | 'off') {
    busy = true;
    failed = false;
    let ok = false;
    try {
      ok = await setPredictiveChoice(choice);
    } catch (e) {
      ok = false;
    } finally {
      busy = false;
    }
    if (ok) open = false;
    else failed = true;
  }
</script>

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open closeOnOutsideClick={false} closeOnEscape={false}>
  <Dialog.Content class="max-w-lg">
    <Dialog.Header>
      <Dialog.Title>{$_('report.predictive.enabled')}</Dialog.Title>
    </Dialog.Header>
    <div class="py-2 space-y-3 text-sm text-muted-foreground">
      <p>{$_('report.predictive.prompt')}</p>
      <!-- The same explanation as the question mark on the Program tab's Predictive Mode badge. -->
      <Popover.Root portal="#shadcn-ui-dialog-target">
        <Popover.Trigger class="inline-flex items-center gap-1.5 p-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring border-0 bg-transparent shadow-none font-medium text-xerteal-700 hover:text-xerteal-600 hover:underline underline-offset-2">
          <i class="fa-regular fa-circle-question" aria-hidden="true"/>{$_('report.predictive.what_is')}
        </Popover.Trigger>
        <!-- No taller than the room on its side (fitViewport), scrolling when that is less, so it is not cut off on a short window -->
        <Popover.Content side="bottom" align="start" collisionPadding={12} fitViewport class="w-[min(26rem,calc(100vw-1.5rem))] min-w-0 text-gray-600 text-sm space-y-2 overflow-y-auto">
          <PredictiveModeSummary {predictive} />
        </Popover.Content>
      </Popover.Root>
      {#if failed}<p class="text-red-700">{$_('report.predictive.save_failed')}</p>{/if}
    </div>
    <Dialog.Footer>
      <Button variant="outline" disabled={busy} on:click={() => choose('off')}>{$_('report.predictive.disable')}</Button>
      <Button disabled={busy} on:click={() => choose('on')} data-main-button>{$_('report.predictive.continue')}</Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
